import { useState, useEffect } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../../config/firebaseConfig";
import { dedouaner } from "../../oilmed.service";
import { fmt, showToast } from "../../utils";

/**
 * DedouanerModal component - modal for customs clearance
 */
export default function DedouanerModal({
  showModal,
  setShowModal,
  stock,
  terminal,
  produit,
}) {
  const ALL_LOTS_VALUE = "__ALL__";
  const EPSILON = 1e-6;
  const [qty, setQty] = useState("");
  const [selectedLot, setSelectedLot] = useState("");
  const [date, setDate] = useState(
    () => new Date().toISOString().split("T")[0],
  );
  const [lots, setLots] = useState([]);
  const [loadingLots, setLoadingLots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const prodLabel = produit === "essence" ? "Super" : "Go";

  function parseDecimalInput(value) {
    const raw = String(value || "").trim();
    if (!raw) return NaN;

    // Accept both comma and dot decimal separators.
    const compact = raw.replace(/[\s\u202F\u00A0]/g, "");
    const normalized = compact.includes(",")
      ? compact.replace(/\./g, "").replace(/,/g, ".")
      : compact;
    return Number(normalized);
  }

  // Fetch available lots when modal opens
  useEffect(() => {
    if (!showModal) return;

    const fetchLots = async () => {
      setLoadingLots(true);
      try {
        const lotsCollection = produit === "essence" ? "lots_su" : "lots_go";
        const lotsRef = collection(db, "terminaux", terminal, lotsCollection);
        const snap = await getDocs(lotsRef);
        const lotsData = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        // Only show lots with remaining volume
        const filteredLots = lotsData.filter((lot) => (lot.volumeM3 || 0) > 0);
        setLots(filteredLots);
        if (filteredLots.length > 0) {
          setSelectedLot(ALL_LOTS_VALUE);
        }
      } catch (err) {
        console.error("Error fetching lots:", err);
        showToast("Erreur lors du chargement des lots", "error");
      } finally {
        setLoadingLots(false);
      }
    };

    fetchLots();
  }, [showModal, terminal, produit]);

  async function handleDedouaner() {
    console.log("[DedouanerModal] Confirm button clicked");
    console.log("[DedouanerModal] selectedLot:", selectedLot);
    console.log("[DedouanerModal] qty:", qty);

    if (!selectedLot) {
      showToast("Sélectionner un lot", "error");
      return;
    }
    const q = parseDecimalInput(qty);
    if (!q || q <= 0) {
      showToast("Saisir une quantité", "error");
      return;
    }
    const selectedLotData = lots.find((lot) => lot.id === selectedLot);
    const maxAllowed =
      selectedLot === ALL_LOTS_VALUE
        ? (stock?.sd ?? 0)
        : (selectedLotData?.volumeM3 ?? 0);

    if (q - maxAllowed > EPSILON) {
      showToast(`Max: ${fmt(maxAllowed)} m³`, "error");
      return;
    }
    setSubmitting(true);
    try {
      console.log("[DedouanerModal] Calling dedouaner with:", {
        terminal,
        produit,
        q,
        selectedLot: selectedLot === ALL_LOTS_VALUE ? null : selectedLot,
      });
      const ref = `DD-${terminal}-${Date.now().toString().slice(-4)}`;
      await dedouaner(
        terminal,
        produit,
        q,
        ref,
        selectedLot === ALL_LOTS_VALUE ? null : selectedLot,
        date,
      );
      showToast(`${fmt(q)} m³ dédouanés → Libre pratique ✔`);
      setShowModal(false);
      setQty("");
    } catch (err) {
      console.error("[DedouanerModal] Error:", err);
      showToast(err.message, "error");
    } finally {
      setSubmitting(false);
    }
  }

  if (!showModal) return null;

  return (
    <div
      className="modal-overlay open"
      onClick={(e) => e.target === e.currentTarget && setShowModal(false)}
    >
      <div className="modal">
        <div className="modal-handle" />
        <div className="modal-title">Dédouaner une partie</div>
        <div className="modal-sub">
          {"// "}
          {terminal} · {prodLabel.toUpperCase()} → LIBRE PRATIQUE
        </div>
        <div className="info-strip">
          Max disponible : <span>{fmt(stock?.sd)} m³</span>
        </div>

        <div className="form-group">
          <label>Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        {/* Lot Selection */}
        <div className="form-group">
          <label>Numéro POT *</label>
          {loadingLots ? (
            <div style={{ padding: "12px", color: "var(--text-muted)" }}>
              Chargement...
            </div>
          ) : lots.length === 0 ? (
            <div style={{ padding: "12px", color: "var(--text-muted)" }}>
              Aucun lot disponible
            </div>
          ) : (
            <select
              value={selectedLot}
              onChange={(e) => setSelectedLot(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 8,
                border: "1px solid var(--border)",
                background: "var(--bg-elevated)",
                color: "var(--text)",
                fontSize: 14,
                fontFamily: "var(--font-mono)",
              }}
            >
              <option value={ALL_LOTS_VALUE}>
                Tous les lots (FIFO) - max {fmt(stock?.sd)} m³
              </option>
              {lots.map((lot) => {
                const remaining = lot.volumeM3 || 0;
                return (
                  <option key={lot.id} value={lot.id}>
                    {lot.numeroPOT} ({fmt(remaining)} m³ restant)
                  </option>
                );
              })}
            </select>
          )}
        </div>

        <div className="form-group">
          <label>Quantité à dédouaner (m³)</label>
          <input
            type="text"
            inputMode="decimal"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            placeholder="Ex: 100 ou 100,5"
          />
        </div>

        <div className="btn-row">
          <button
            className="btn btn-gold"
            style={{ flex: 1 }}
            disabled={submitting}
            onClick={handleDedouaner}
          >
            {submitting ? "En cours…" : "✔ Confirmer"}
          </button>
          <button className="btn btn-ghost" onClick={() => setShowModal(false)}>
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}
